import { useState, useRef, useEffect } from 'react';
import { X, Save, Upload, Image as ImageIcon, FileSignature, Minus, Trash2, FolderOpen } from 'lucide-react';
import html2canvas from 'html2canvas';
import ColorPicker from './ColorPicker';
import CertificatePreview from './CertificatePreview';
import { isPortraitTemplate, isDualInstructorTemplate } from './templateConfigs';

interface CertificateTemplateData {
  name: string;
  templateType: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22; // 1: 테두리, 2: 없음, 3: 강사2명, 4: 강사1명, 5: 강사2명 serif, 6: 강사1명 손글씨, 7: 다크·워터마크
  borderColor: string;
  borderThickness: number;
  logoUrl: string | null;
  courseName: string;
  signatureUrl: string | null;
  signerName: string;
  signerTitle: string;
  // template_type 3 전용: 두 번째 강사
  signatureUrl2: string | null;
  signerName2: string;
  thumbnailUrl?: string; // 미리보기 썸네일 이미지
}

interface CertificateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: CertificateTemplateData, templateId: string) => void;
  initialData?: CertificateTemplateData;
  totalHours?: number;
  className?: string;
}

export default function CertificateTemplateModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  totalHours,
}: CertificateTemplateModalProps) {
  const [templateData, setTemplateData] = useState<CertificateTemplateData>(
    initialData || {
      name: '',
      templateType: 1,
      borderColor: '#C41E3A',
      borderThickness: 4,
      logoUrl: null,
      courseName: '',
      signatureUrl: null,
      signerName: '',
      signerTitle: '',
      signatureUrl2: null,
      signerName2: '',
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<'logo' | 'signature' | 'signature2' | null>(null);

  // 저장된 템플릿 목록
  const [savedTemplates, setSavedTemplates] = useState<Array<CertificateTemplateData & { id: string }>>([]);
  const [hoveredTemplateId, setHoveredTemplateId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);
  const signature2InputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  // initialData가 변경되면 templateData 업데이트
  useEffect(() => {
    if (initialData) {
      setTemplateData(initialData);
    }
  }, [initialData]);

  // 저장된 템플릿 불러오기 (API 연동)
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  // API: 템플릿 목록 불러오기
  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/v1/provider/certificate-templates', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const templates = data.templates || [];

        // API 응답을 컴포넌트 형식으로 변환
        const formattedTemplates = templates.map((t: any) => ({
          id: t.id,
          name: t.name,
          templateType: t.template_type || 1,
          borderColor: t.border_color,
          borderThickness: t.border_thickness,
          logoUrl: t.logo_url,
          courseName: t.course_name,
          signatureUrl: t.signature_url,
          signerName: t.signer_name,
          signerTitle: t.signer_title,
          signatureUrl2: t.signature_url_2 ?? null,
          signerName2: t.signer_name_2 ?? '',
          thumbnailUrl: t.thumbnail_url,
        }));

        setSavedTemplates(formattedTemplates);
      } else {
        console.error('Failed to load templates');
        setSavedTemplates([]);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      setSavedTemplates([]);
    }
  };

  if (!isOpen) return null;

  // 필드 업데이트
  const updateField = <K extends keyof CertificateTemplateData>(
    field: K,
    value: CertificateTemplateData[K]
  ) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));

    // 에러 제거
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // 이미지 업로드 처리
  const handleImageUpload = async (type: 'logo' | 'signature' | 'signature2', file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB를 초과할 수 없습니다.');
      return;
    }

    setUploading(type);

    try {
      // FormData로 파일 전송
      const formData = new FormData();
      formData.append('file', file);
      // 업로드 버킷 분류상 logo/signature만 존재 → signature2도 signature로 전송
      formData.append('type', type === 'logo' ? 'logo' : 'signature');

      const response = await fetch('/api/v1/provider/certificate-templates/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.url) {
        if (type === 'logo') {
          updateField('logoUrl', data.url);
        } else if (type === 'signature2') {
          updateField('signatureUrl2', data.url);
        } else {
          updateField('signatureUrl', data.url);
        }
      } else {
        throw new Error(data.error || '업로드에 실패했습니다.');
      }

      setUploading(null);
    } catch (error: any) {
      console.error(`Failed to upload ${type}:`, error);
      alert(`${type === 'logo' ? '로고' : '서명'} 업로드에 실패했습니다: ${error.message}`);
      setUploading(null);
    }
  };

  // 이미지 제거
  const handleRemoveImage = (type: 'logo' | 'signature' | 'signature2') => {
    if (type === 'logo') {
      updateField('logoUrl', null);
    } else if (type === 'signature2') {
      updateField('signatureUrl2', null);
    } else {
      updateField('signatureUrl', null);
    }
  };

  // 템플릿 타입 변경 — 강사 수/직위 차이에 따라 불필요한 값 정리
  const handleTemplateTypeChange = (type: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22) => {
    const dual = isDualInstructorTemplate(type); // 강사 2명 (config 기준: 3·5·14)
    const noTitle = type >= 3; // 직위 없음 (템플릿 3 이상 전부)
    setTemplateData((prev) => {
      const next = { ...prev, templateType: type };
      if (noTitle) next.signerTitle = '';
      // 강사 2명 템플릿(3·5)만 두 번째 강사 유지, 그 외엔 제거
      if (!dual) {
        next.signatureUrl2 = null;
        next.signerName2 = '';
      }
      return next;
    });
    // 타입 변경 시 관련 에러 정리
    setErrors((prev) => {
      const e = { ...prev };
      delete e.signerTitle;
      delete e.signatureUrl2;
      delete e.signerName2;
      return e;
    });
  };

  // 유효성 검사
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!templateData.name.trim()) {
      newErrors.name = '템플릿 이름을 입력해주세요.';
    }

    if (!templateData.logoUrl) {
      newErrors.logoUrl = '로고 이미지를 업로드해주세요.';
    }

    if (!templateData.courseName.trim()) {
      newErrors.courseName = '코스명을 입력해주세요.';
    }

    if (!templateData.signatureUrl) {
      newErrors.signatureUrl = '서명 이미지를 업로드해주세요.';
    }

    const isDual = isDualInstructorTemplate(templateData.templateType);
    const noTitle = templateData.templateType >= 3;

    if (!templateData.signerName.trim()) {
      newErrors.signerName = noTitle ? '강사 1 이름 또는 직책을 입력해주세요.' : '담당자 이름을 입력해주세요.';
    }

    if (isDual) {
      // 강사 2명 템플릿: 직위 불필요, 두 번째 강사 서명·이름 필수
      if (!templateData.signatureUrl2) {
        newErrors.signatureUrl2 = '강사 2 서명 이미지를 업로드해주세요.';
      }
      if (!templateData.signerName2.trim()) {
        newErrors.signerName2 = '강사 2 이름 또는 직책을 입력해주세요.';
      }
    } else if (!noTitle && !templateData.signerTitle.trim()) {
      newErrors.signerTitle = '담당자 직위를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 저장
  const handleSave = async () => {
    if (!validate()) {
      alert('필수 입력 항목을 모두 입력해주세요.');
      return;
    }

    setLoading(true);

    // 캡처 시 미리보기 폭을 디자인 기준폭으로 고정 → 폰트(vw·max)와 컨테이너 비율이 항상 일치.
    // (모달 컬럼이 좁으면 vw 폰트가 상대적으로 커져 코스명이 줄바꿈되는 문제 방지)
    const captureWidth = isPortraitTemplate(templateData.templateType) ? 723 : 1016;
    const prevInlineWidth = previewRef.current?.style.width ?? '';

    try {
      // 1. 캡처 모드 활성화 (인터랙티브 요소 숨김) + 캡처 폭 고정
      setCaptureMode(true);
      if (previewRef.current) previewRef.current.style.width = `${captureWidth}px`;

      // 2. DOM 업데이트 대기 (더 길게)
      await new Promise(resolve => setTimeout(resolve, 300));

      // 3. 미리보기 영역 캡처해서 썸네일 생성
      let thumbnailUrl = '';
      if (previewRef.current) {
        console.log('Capturing certificate preview...');
        console.log('Preview element:', previewRef.current);
        console.log('Element dimensions:', previewRef.current.offsetWidth, 'x', previewRef.current.offsetHeight);

        const canvas = await html2canvas(previewRef.current, {
          scale: 2, // 고해상도로 캡처
          backgroundColor: '#ffffff',
          logging: true, // 디버깅을 위해 로깅 활성화
          useCORS: true,
          allowTaint: true,
        });

        // Canvas를 Blob으로 변환 (고품질 JPEG)
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.95);
        });

        // 2. 썸네일을 Supabase Storage에 업로드
        const formData = new FormData();
        formData.append('file', blob, 'thumbnail.jpg');
        formData.append('type', 'thumbnail');

        const uploadResponse = await fetch('/api/v1/provider/certificate-templates/upload', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        const uploadData = await uploadResponse.json();

        if (uploadResponse.ok && uploadData.url) {
          thumbnailUrl = uploadData.url;
        } else {
          console.warn('Thumbnail upload failed, continuing without thumbnail');
        }
      }

      // 3. 템플릿 데이터를 API에 저장
      const response = await fetch('/api/v1/provider/certificate-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: templateData.name,
          templateType: templateData.templateType,
          borderColor: templateData.borderColor,
          borderThickness: templateData.borderThickness,
          logoUrl: templateData.logoUrl,
          courseName: templateData.courseName,
          signatureUrl: templateData.signatureUrl,
          signerName: templateData.signerName,
          signerTitle: templateData.signerTitle,
          signatureUrl2: isDualInstructorTemplate(templateData.templateType) ? templateData.signatureUrl2 : null,
          signerName2: isDualInstructorTemplate(templateData.templateType) ? templateData.signerName2 : '',
          thumbnailUrl,
          isDefault: false,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('템플릿이 저장되었습니다.');
        await loadTemplates(); // 템플릿 목록 새로고침
        // 캡처한 썸네일 URL을 함께 넘겨 부모 미리보기가 라이브 렌더 대신 이미지로 표시하도록
        onSave({ ...templateData, thumbnailUrl }, data.template.id);
        onClose();
      } else {
        throw new Error(data.error || '템플릿 저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('Failed to save template:', error);
      alert(`템플릿 저장 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setCaptureMode(false); // 캡처 모드 해제
      if (previewRef.current) previewRef.current.style.width = prevInlineWidth; // 고정 폭 복원
      setLoading(false);
    }
  };

  // 템플릿 선택 (불러오기)
  const handleSelectTemplate = (template: CertificateTemplateData & { id: string }) => {
    const { id, ...templateWithoutId } = template;
    setTemplateData(templateWithoutId);
    setErrors({});
  };

  // 템플릿 삭제
  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const response = await fetch(`/api/v1/provider/certificate-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        // 로컬 상태 업데이트
        const updatedTemplates = savedTemplates.filter((t) => t.id !== templateId);
        setSavedTemplates(updatedTemplates);
        setDeleteConfirmId(null);
        setHoveredTemplateId(null);
        alert('템플릿이 삭제되었습니다.');
      } else {
        const data = await response.json();
        throw new Error(data.error || '템플릿 삭제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      alert(`템플릿 삭제 중 오류가 발생했습니다: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-primary text-white">
          <h2 className="text-2xl font-bold">인증서 템플릿 편집</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 템플릿 선택 섹션 (좌우 분할, 각각 독립 카드로 구분) */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-100">
          <div className={`grid gap-4 ${savedTemplates.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {/* 좌측: 빈 템플릿 선택 — 흰 카드 + 네이비 악센트 */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 leading-tight">빈 템플릿 선택</h3>
                  <p className="text-[11px] text-gray-500 leading-tight">새 인증서 디자인으로 시작</p>
                </div>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 template-scroll">
                {/* 템플릿 1 */}
                <button
                  onClick={() => handleTemplateTypeChange(1)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 1
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_1.png"
                    alt="템플릿 1"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 1 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 1</p>
                  </div>
                </button>

                {/* 템플릿 2 */}
                <button
                  onClick={() => handleTemplateTypeChange(2)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 2
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_2.png"
                    alt="템플릿 2"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 2 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 2</p>
                  </div>
                </button>

                {/* 템플릿 3 — 강사 2명 */}
                <button
                  onClick={() => handleTemplateTypeChange(3)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 3
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_3.png"
                    alt="템플릿 3"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 3 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 3 (강사 2명)</p>
                  </div>
                </button>

                {/* 템플릿 4 — 강사 1명 */}
                <button
                  onClick={() => handleTemplateTypeChange(4)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 4
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_4.png"
                    alt="템플릿 4"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 4 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 4 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 5 — 강사 2명 (serif) */}
                <button
                  onClick={() => handleTemplateTypeChange(5)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 5
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_5.png"
                    alt="템플릿 5"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 5 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 5 (강사 2명)</p>
                  </div>
                </button>

                {/* 템플릿 6 — 강사 1명 (손글씨) */}
                <button
                  onClick={() => handleTemplateTypeChange(6)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 6
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_6.png"
                    alt="템플릿 6"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 6 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 6 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 7 — 강사 1명 (다크·워터마크) */}
                <button
                  onClick={() => handleTemplateTypeChange(7)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 7
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_7.png"
                    alt="템플릿 7"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 7 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 7 (다크)</p>
                  </div>
                </button>

                {/* 템플릿 8 — 강사 1명 (크림·얇은 폰트) */}
                <button
                  onClick={() => handleTemplateTypeChange(8)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 8
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_8.png"
                    alt="템플릿 8"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 8 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 8 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 9 — 강사 1명 (아르데코·워터마크) */}
                <button
                  onClick={() => handleTemplateTypeChange(9)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 9
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_9.png"
                    alt="템플릿 9"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 9 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 9 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 10 — 강사 1명 (오너먼트) */}
                <button
                  onClick={() => handleTemplateTypeChange(10)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 10
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_10.png"
                    alt="템플릿 10"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 10 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 10 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 11 — 강사 1명 (웨이브·워터마크) */}
                <button
                  onClick={() => handleTemplateTypeChange(11)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 11
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_11.png"
                    alt="템플릿 11"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 11 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 11 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 12 — 강사 1명 (네이비·워터마크) */}
                <button
                  onClick={() => handleTemplateTypeChange(12)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 12
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_12.png"
                    alt="템플릿 12"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 12 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 12 (강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 13 — 세로형·강사 1명 (Certificate of Completion) */}
                <button
                  onClick={() => handleTemplateTypeChange(13)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 13
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_13.png"
                    alt="템플릿 13"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 13 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 13 (세로·강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 14 — 세로형·강사 2명 (Certificate of Completion) */}
                <button
                  onClick={() => handleTemplateTypeChange(14)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 14
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_14.png"
                    alt="템플릿 14"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 14 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 14 (세로·강사 2명)</p>
                  </div>
                </button>

                {/* 템플릿 15 — 세로형·강사 1명 (Certificate of Qualification·좌측 네이비) */}
                <button
                  onClick={() => handleTemplateTypeChange(15)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 15
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_15.png"
                    alt="템플릿 15"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 15 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 15 (세로·강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 16 — 세로형·강사 1명 (Participation·골드 오너먼트) */}
                <button
                  onClick={() => handleTemplateTypeChange(16)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 16
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_16.png"
                    alt="템플릿 16"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 16 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 16 (세로·강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 17 — 세로형·강사 1명 (Completion·실버 크라운) */}
                <button
                  onClick={() => handleTemplateTypeChange(17)}
                  className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                    templateData.templateType === 17
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-gray-300 hover:border-primary'
                  }`}
                  style={{ width: '169.68px', height: '120px' }}
                >
                  <img
                    src="/certificate-templates/template_17.png"
                    alt="템플릿 17"
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                  {templateData.templateType === 17 && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-white text-xs font-medium text-center">템플릿 17 (세로·강사 1명)</p>
                  </div>
                </button>

                {/* 템플릿 18~22 — 세로형·강사 1명 */}
                {([
                  [18, '템플릿 18 (세로·강사 1명)'],
                  [19, '템플릿 19 (세로·강사 1명)'],
                  [20, '템플릿 20 (세로·강사 1명)'],
                  [21, '템플릿 21 (세로·강사 1명)'],
                  [22, '템플릿 22 (세로·강사 1명)'],
                ] as const).map(([num, label]) => (
                  <button
                    key={num}
                    onClick={() => handleTemplateTypeChange(num)}
                    className={`relative flex-shrink-0 bg-white border-2 rounded-lg overflow-hidden transition-all shadow-sm hover:shadow-md ${
                      templateData.templateType === num
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'border-gray-300 hover:border-primary'
                    }`}
                    style={{ width: '169.68px', height: '120px' }}
                  >
                    <img
                      src={`/certificate-templates/template_${num}.png`}
                      alt={`템플릿 ${num}`}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                    {templateData.templateType === num && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                      <p className="text-white text-xs font-medium text-center">{label}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 우측: 저장된 템플릿 불러오기 — 오렌지 톤 카드 + 악센트 */}
            {savedTemplates.length > 0 && (
              <div className="bg-orange-50 rounded-xl border border-orange-300 shadow-sm p-4">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-4 h-4 text-accent" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                      저장된 템플릿 불러오기 <span className="text-xs font-normal text-gray-500">({savedTemplates.length}개)</span>
                    </h3>
                    <p className="text-[11px] text-gray-500 leading-tight">이전에 저장한 설정 불러오기</p>
                  </div>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 template-scroll">
                  {savedTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="relative flex-shrink-0 group"
                      onMouseEnter={() => setHoveredTemplateId(template.id)}
                      onMouseLeave={() => setHoveredTemplateId(null)}
                    >
                      {/* 썸네일 - A4 가로 비율 (1.414:1) 유지 */}
                      <button
                        onClick={() => handleSelectTemplate(template)}
                        className="relative bg-white border-2 border-gray-300 rounded-lg overflow-hidden hover:border-primary transition-all shadow-sm hover:shadow-md"
                        style={{ width: '169.68px', height: '120px' }}
                      >
                        {/* 캡처된 썸네일 이미지 */}
                        {template.thumbnailUrl ? (
                          <img
                            src={template.thumbnailUrl}
                            alt={template.name}
                            className="absolute inset-0 w-full h-full object-contain"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                            <p className="text-xs text-gray-500">미리보기 없음</p>
                          </div>
                        )}
                        {/* 템플릿 이름 오버레이 */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                          <p className="text-white text-xs font-medium truncate">{template.name}</p>
                        </div>
                      </button>

                      {/* 삭제 버튼 (호버 시 표시) */}
                      {hoveredTemplateId === template.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(template.id);
                          }}
                          className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors flex items-center justify-center z-10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 삭제 확인 모달 */}
        {deleteConfirmId && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">템플릿 삭제</h3>
              <p className="text-sm text-gray-600 mb-6">
                이 템플릿을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  취소
                </button>
                <button
                  onClick={() => handleDeleteTemplate(deleteConfirmId)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  삭제
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
            {/* 좌측: 편집 폼 */}
            <div className="space-y-6">
              {/* 템플릿 이름 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  템플릿 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="예: 필라테스 지도자 과정 인증서"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* 테두리 색상 & 두께 (템플릿 1만) */}
              {templateData.templateType === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      테두리 색상 <span className="text-red-500">*</span>
                    </label>
                    <ColorPicker
                      value={templateData.borderColor}
                      onChange={(color) => updateField('borderColor', color)}
                    />
                  </div>

                  {/* 테두리 두께 */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      테두리 두께 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={templateData.borderThickness}
                        onChange={(e) => updateField('borderThickness', parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={templateData.borderThickness}
                          onChange={(e) => updateField('borderThickness', parseInt(e.target.value) || 1)}
                          className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary"
                        />
                        <span className="text-sm text-gray-600">px</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* 로고 업로드 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  로고 이미지 <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">배경색이 없는 PNG 파일로 업로드하시는 것을 권장합니다.</p>
                <div className="flex items-center gap-3">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files && handleImageUpload('logo', e.target.files[0])}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading === 'logo'}
                    className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors flex items-center justify-center gap-2 text-gray-700 hover:text-primary disabled:opacity-50"
                  >
                    {uploading === 'logo' ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        업로드 중...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        로고 업로드
                      </>
                    )}
                  </button>
                  {templateData.logoUrl && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage('logo')}
                      className="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                    >
                      <Minus className="w-5 h-5" />
                      제거
                    </button>
                  )}
                </div>
                {errors.logoUrl && <p className="text-red-500 text-sm mt-1">{errors.logoUrl}</p>}
                {templateData.logoUrl && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">로고가 업로드되었습니다</span>
                  </div>
                )}
              </div>

              {/* 코스명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  코스명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateData.courseName}
                  onChange={(e) => updateField('courseName', e.target.value)}
                  placeholder="예: Pilates Instructor Certification"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.courseName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.courseName && (
                  <p className="text-red-500 text-sm mt-1">{errors.courseName}</p>
                )}
              </div>

              {/* 서명 업로드 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  서명 이미지 <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    ref={signatureInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      e.target.files && handleImageUpload('signature', e.target.files[0])
                    }
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => signatureInputRef.current?.click()}
                    disabled={uploading === 'signature'}
                    className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors flex items-center justify-center gap-2 text-gray-700 hover:text-primary disabled:opacity-50"
                  >
                    {uploading === 'signature' ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                        업로드 중...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        서명 업로드
                      </>
                    )}
                  </button>
                  {templateData.signatureUrl && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage('signature')}
                      className="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                    >
                      <Minus className="w-5 h-5" />
                      제거
                    </button>
                  )}
                </div>
                {errors.signatureUrl && (
                  <p className="text-red-500 text-sm mt-1">{errors.signatureUrl}</p>
                )}
                {templateData.signatureUrl && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                    <FileSignature className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">서명이 업로드되었습니다</span>
                  </div>
                )}
              </div>

              {/* 담당자/강사 1 이름 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {templateData.templateType >= 3 ? '강사 1 이름 또는 직책' : '담당자 이름'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateData.signerName}
                  onChange={(e) => updateField('signerName', e.target.value)}
                  placeholder="예: 홍길동"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.signerName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.signerName && (
                  <p className="text-red-500 text-sm mt-1">{errors.signerName}</p>
                )}
              </div>

              {/* 담당자 직위 (직위 없는 템플릿 3·4에서는 숨김) */}
              {templateData.templateType < 3 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    담당자 직위 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={templateData.signerTitle}
                    onChange={(e) => updateField('signerTitle', e.target.value)}
                    placeholder="예: CEO, Director"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.signerTitle ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.signerTitle && (
                    <p className="text-red-500 text-sm mt-1">{errors.signerTitle}</p>
                  )}
                </div>
              )}

              {/* 강사 2 서명 + 이름 (강사 2명 템플릿 3·5·14 전용) */}
              {isDualInstructorTemplate(templateData.templateType) && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      강사 2 서명 이미지 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        ref={signature2InputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          e.target.files && handleImageUpload('signature2', e.target.files[0])
                        }
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => signature2InputRef.current?.click()}
                        disabled={uploading === 'signature2'}
                        className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors flex items-center justify-center gap-2 text-gray-700 hover:text-primary disabled:opacity-50"
                      >
                        {uploading === 'signature2' ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                            업로드 중...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            강사 2 서명 업로드
                          </>
                        )}
                      </button>
                      {templateData.signatureUrl2 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveImage('signature2')}
                          className="px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
                        >
                          <Minus className="w-5 h-5" />
                          제거
                        </button>
                      )}
                    </div>
                    {errors.signatureUrl2 && (
                      <p className="text-red-500 text-sm mt-1">{errors.signatureUrl2}</p>
                    )}
                    {templateData.signatureUrl2 && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                        <FileSignature className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">강사 2 서명이 업로드되었습니다</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      강사 2 이름 또는 직책 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={templateData.signerName2}
                      onChange={(e) => updateField('signerName2', e.target.value)}
                      placeholder="예: 김철수"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.signerName2 ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.signerName2 && (
                      <p className="text-red-500 text-sm mt-1">{errors.signerName2}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* 우측: 미리보기 */}
            <div className="lg:sticky lg:top-6 lg:self-start">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">미리보기</h3>
              <CertificatePreview
                ref={previewRef}
                templateType={templateData.templateType}
                borderColor={templateData.borderColor}
                borderThickness={templateData.borderThickness}
                logoUrl={templateData.logoUrl}
                signatureUrl={templateData.signatureUrl}
                signatureUrl2={templateData.signatureUrl2}
                courseName={templateData.courseName}
                signerName={templateData.signerName}
                signerTitle={templateData.signerTitle}
                signerName2={templateData.signerName2}
                totalHours={totalHours}
                captureMode={captureMode}
              />
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className={`px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors font-medium flex items-center gap-2 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Save className="w-5 h-5" />
            {loading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
